import { Dispatch, SetStateAction } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input, PrimaryButton, SecondaryButton, Typography } from '@weareopenr/component-library';
import { AttributeFields } from 'src/components/AttributeFields/AttributeFields';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import { UNIQUE_ATTRIBUTE_RULE_NAME_ERROR } from '../constants';
import styles from './Steps.module.scss';

export interface IRuleDetailsValues {
  name: string;
  consumerName: string;
  description: string;
  plu: string;
}

interface RuleDetailsStepProps {
  form: UseFormReturn<IRuleDetailsValues>;
  image: string | File;
  setImage: Dispatch<SetStateAction<string | File>>;
  placeholders: {
    consumer_name?: string;
    description?: string;
    posId?: string;
  };
  existingRuleNames: string[];
  onCancel: () => void;
  onContinue: () => void;
}

export const RuleDetailsStep = ({
  form: {
    register,
    watch,
    formState: { errors },
  },
  image,
  setImage,
  placeholders,
  existingRuleNames,
  onCancel,
  onContinue,
}: RuleDetailsStepProps) => {
  const [name, consumerName, description, plu] = watch(['name', 'consumerName', 'description', 'plu']);
  const hasAttributeValue = [consumerName, description, plu].some((value) => value.trim()) || image instanceof File;
  const canContinue = name.trim().length > 0 && !errors.name && hasAttributeValue;

  const isNameTaken = (value: string) =>
    existingRuleNames.some((ruleName) => ruleName.toLocaleLowerCase() === value.trim().toLocaleLowerCase());

  return (
    <div className={styles['content']}>
      <Typography weight="bold" className={styles['step']}>
        Step 1 of 2
      </Typography>
      <Typography size="xxlarge" weight="semi-bold" className={styles['title']}>
        Create attribute rule
      </Typography>

      <Input
        type="text"
        label="Rule Name"
        register={register('name', {
          validate: (value) => !isNameTaken(value) || UNIQUE_ATTRIBUTE_RULE_NAME_ERROR,
        })}
        error={errors.name?.message}
      />

      <div className={styles['divider']} />

      <Typography weight="bold" className={styles['section-title']}>
        Product Attributes
      </Typography>
      <AttributeFields register={register} image={image} setImage={setImage} placeholders={placeholders} />

      <div className={styles['actions']}>
        <ButtonContainer
          margin="none"
          left={
            <SecondaryButton buttonStyle="outline" type="button" size="full-width" onClick={onCancel}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              type="button"
              colour="blueberry"
              size="full-width"
              disabled={!canContinue}
              onClick={onContinue}
            >
              Continue
            </PrimaryButton>
          }
        />
      </div>
    </div>
  );
};

export default RuleDetailsStep;
